// What the planner has picked, and what "picked" means when several things are picked at once.
//
// ── WHY A SELECTION MODEL AT ALL ────────────────────────────────────────────────
// Until now every gesture acted on the one chip it started from. That is fine for a demo and wrong for
// a working board: a planner rescheduling a rained-off Tuesday wants to grab the whole day and push it,
// not repeat one gesture eleven times. Every tool this app is measured against — Monday, ClickUp, Asana
// — treats "the things I have selected" as a first-class noun, and so does this.
//
// ── THE UNIT OF SELECTION IS ONE DAY ────────────────────────────────────────────
// It used to be a whole bar of work, and that was right while hours were spread evenly across a
// window: a day had no independent existence, so the only honest unit was the job. Now that a day is
// a row you can drag, resize, split and delete on its own, selecting one day and lighting up six
// would be the UI insisting on a model the database no longer has.
//
// A command that changes the WINDOW (move, resize, next week) must then act on distinct workpackages,
// because the window belongs to the job and not to the person; a command that changes WHO acts on the
// pairs. `workpackages()` and `pairs()` below make that difference explicit at every call site rather
// than leaving it to be remembered.
import type { ScheduleItem } from '../api/planning.ts';

/** `workpackageId:resourceId:day` — one person's work on one job on ONE DAY. */
export type SelKey = string;

/**
 * ── THE UNIT USED TO BE A BAR AND IS NOW A DAY ──────────────────────────────────
 * While hours were spread evenly across a window, a day had no independent existence: the only thing
 * you could move was the whole job, so selecting a whole job was the only honest unit. Now that a day
 * is a row that can be dragged, resized, split and deleted on its own, selecting one day and getting
 * six highlighted would be the UI insisting on a model the database no longer has.
 *
 * Commands that act on the WINDOW still reduce to distinct workpackages, and commands that act on WHO
 * still reduce to distinct pairs — see `workpackages()` and `pairs()`. Only the thing a planner points
 * at has changed.
 */
export const selKey = (i: { workpackageId: number; resourceId: number; day?: string }): SelKey =>
  `${i.workpackageId}:${i.resourceId}:${i.day ?? ''}`;

export interface Selection {
  keys: ReadonlySet<SelKey>;
  /** The last thing clicked without a modifier. Shift-click selects the range from here. */
  anchor: SelKey | null;
}

export const EMPTY_SELECTION: Selection = { keys: new Set(), anchor: null };

export const isSelected = (
  s: Selection, i: { workpackageId: number; resourceId: number; day?: string },
): boolean => s.keys.has(selKey(i));

/**
 * A click, with whatever modifiers were held.
 *
 * ── THE THREE BEHAVIOURS EVERY BOARD HAS ────────────────────────────────────────
 *   plain        — replace the selection with this one. Clicking a chip you already had selected
 *                  alongside others narrows to it, which is what every file manager does.
 *   ctrl / cmd   — toggle this one, leave the rest. The way you build an irregular set.
 *   shift        — extend from the anchor across the VISIBLE ORDER, not across ids. `ordered` is the
 *                  list as the screen lays it out, so shift-click means "everything between these two
 *                  as I see them" — the only definition that matches what the planner is pointing at.
 */
export function clickSelect(
  s: Selection, target: SelKey, mods: { shift?: boolean; meta?: boolean }, ordered: SelKey[],
): Selection {
  if (mods.shift && s.anchor) {
    const a = ordered.indexOf(s.anchor);
    const b = ordered.indexOf(target);
    if (a !== -1 && b !== -1) {
      const [lo, hi] = a <= b ? [a, b] : [b, a];
      // ADDS TO the existing set rather than replacing it, so shift-click after ctrl-click extends the
      // set you were building instead of throwing it away.
      const keys = new Set(s.keys);
      for (const k of ordered.slice(lo, hi + 1)) keys.add(k);
      return { keys, anchor: s.anchor };
    }
  }
  if (mods.meta) {
    const keys = new Set(s.keys);
    if (keys.has(target)) keys.delete(target); else keys.add(target);
    // The anchor follows the last thing TOUCHED, even when that was a removal: a shift-click after a
    // ctrl-click should extend from where the hand last was.
    return { keys, anchor: target };
  }
  return { keys: new Set([target]), anchor: target };
}

/** Everything currently visible, in screen order. `Ctrl+A`. */
export const selectAll = (ordered: SelKey[]): Selection =>
  ({ keys: new Set(ordered), anchor: ordered[ordered.length - 1] ?? null });

/**
 * Resolve the selection back to schedule rows.
 *
 * A selection survives a refetch — the keys are stable, the row objects are not — so this is called on
 * every render against the freshest data rather than caching the items themselves. That also means a
 * selected bar that somebody else deleted simply drops out, instead of a stale object being acted on.
 */
export function resolve(s: Selection, items: ScheduleItem[]): ScheduleItem[] {
  return items.filter((i) => s.keys.has(selKey(i)));
}

/**
 * The distinct JOBS in a selection, each represented by its earliest visible row.
 *
 * For anything that moves a window. Six rows of a two-person three-day job must produce ONE move, or
 * the second call fights the first over the same revision and loses with a 409.
 */
export function workpackages(items: ScheduleItem[]): ScheduleItem[] {
  const byWp = new Map<number, ScheduleItem>();
  for (const i of items) {
    const prev = byWp.get(i.workpackageId);
    if (!prev || i.day < prev.day) byWp.set(i.workpackageId, i);
  }
  return [...byWp.values()];
}

/** The selected DAYS themselves — what a drag, a resize or a delete acts on. */
export function days(items: ScheduleItem[]): ScheduleItem[] {
  return [...items].sort((a, b) => a.day.localeCompare(b.day));
}

/**
 * The distinct (job, person) PAIRS, each represented by one row.
 *
 * For anything that changes who is on the work. Three days of one person on one job is one assignment,
 * and withdrawing it three times would fail twice.
 */
export function pairs(items: ScheduleItem[]): ScheduleItem[] {
  // ITS OWN KEY, NOT `selKey`. Since the selection unit became a day, `selKey` carries the date —
  // reusing it here would make three days of one person's work look like three assignments, and
  // withdrawing one would be attempted three times.
  const byPair = new Map<string, ScheduleItem>();
  for (const i of items) {
    const k = `${i.workpackageId}:${i.resourceId}`;
    const prev = byPair.get(k);
    if (!prev || i.day < prev.day) byPair.set(k, i);
  }
  return [...byPair.values()];
}

/** Drop selected keys that no longer exist, so a deleted bar cannot linger in the set. */
export function prune(s: Selection, items: ScheduleItem[]): Selection {
  const live = new Set(items.map(selKey));
  if ([...s.keys].every((k) => live.has(k))) return s;   // identity when nothing changed: no re-render
  const keys = new Set([...s.keys].filter((k) => live.has(k)));
  return { keys, anchor: s.anchor && keys.has(s.anchor) ? s.anchor : null };
}
